// Global Types
import type {
  $GeoLocation,
} from 'common.types';
import type {
  $FullRegattaGroup,
  $FullRegion,
  $FullWebRegatta,
  $Model,
} from 'common.data';
import type {
  $Translate,
} from 'common.language';

// Global Enums
import {
  webPages,
  webSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import Timeline from 'react-lms-vis-timeline';

// Helpers
import _ from 'lodash';
import moment from 'moment';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  isDiff,
  scrollTop,
  sleep,
} from '@vroskus/library-helpers/dist/browser';
import {
  i18next,
} from 'common.language';
import {
  formatDateRange,
  getDateRange,
  getRegionByRegionSeasonId,
  getRegionSeasonByRegionSeasonId,
  isLatestRegionSeason,
} from 'common.helpers/dist/browser';
import {
  getLocalItemBasePath,
} from '../../../../helpers';
import config from '../../../../config';

// Services
import Services from '../../../../services';

// Components
import Section from '../../../layout/Section';
import Loader from '../../../layout/Loader';
import EmptyList from '../../../common/EmptyList';
import RegattasList from '../../../common/RegattasList';
import FilterSelect from '../../../common/FilterSelect';
import MapCluster from '../../../common/MapCluster';
import NoData from '../../../common/NoData';

// Enums
import {
  statusLabelClass,
} from '../../../../enums';

// Types
import type {
  $ReducerState,
} from '../../../../redux';
import type {
  $RedirectAction,
  $SetRegionAction,
} from '../../../../redux/modules/main/actions';

export type $OwnProps = $Translate & {
  RegionSeasonId: string;
};

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = {
  redirect: $RedirectAction;
  setRegion: $SetRegionAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $FilterView = 'group' | 'map' | 'search';

type $State = {
  activeRegionSeasonId: string;
  data: {
    Locations: Array<$Model<'RegionSeasonRegattaLocation'>>;
    Regattas: Array<$FullWebRegatta>;
  };
  filteredData: Array<$FullWebRegatta>;
  filterValue: string;
  filterView: $FilterView;
  loading: boolean;
  reCenter: $GeoLocation | Date | null;
  regionsGeoData: Array<{
    center: $GeoLocation;
    flag: string;
    path: Array<Array<$GeoLocation>>;
    zoom: number;
  }>;
  regionsLoading: boolean;
  showFullList: boolean;
  subSectionView: 'map' | 'timeline' | null;
};

const listSubSectionId = `${webSections[webPages.regattasPage].regattas}-list`;

class RegattasSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      activeRegionSeasonId: props.RegionSeasonId,
      data: {
        Locations: [],
        Regattas: [],
      },
      filteredData: [],
      filterValue: '',
      filterView: 'search',
      loading: false,
      reCenter: null,
      regionsGeoData: [],
      regionsLoading: false,
      showFullList: false,
      subSectionView: null,
    };
  }

  componentDidMount(): void {
    this.init();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      RegionSeasonId,
    } = this.props;

    if (isDiff(
      prevProps.RegionSeasonId,
      RegionSeasonId,
    )) {
      this.setActiveRegionSeason({
        RegionSeasonId,
      });
    }
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      activeRegionSeasonId,
    } = this.state;

    try {
      await this.setStateAsync({
        loading: true,
      });

      const data = await Services.Api.getRegattasRegattasSectionData({
        RegionSeasonId: activeRegionSeasonId,
      });

      await this.setStateAsync({
        data,
        filteredData: data.Regattas,
        loading: false,
      });
    } catch (error) {
      await this.setStateAsync({
        loading: false,
      });

      ErrorLog.exception(error as Error);
    }
  }

  async getRegionsData(): Promise<void> {
    try {
      await this.setStateAsync({
        regionsLoading: true,
      });

      const response = await fetch(`${config.CDN_URL}/static/regions.json`);
      const responseData = await response.json();

      await this.setStateAsync({
        regionsGeoData: responseData,
        regionsLoading: false,
      });
    } catch (error) {
      await this.setStateAsync({
        regionsLoading: false,
      });

      ErrorLog.exception(error as Error);
    }
  }

  setupSocket(): void {
    const {
      activeRegionSeasonId,
    } = this.state;

    Services.Socket.on({
      action: (data) => {
        if (data.RegionSeasonId === activeRegionSeasonId && data.modelName === 'Regatta') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  getGroups(): Array<$Model<'Group'>> {
    const {
      data,
    } = this.state;

    const output: Array<$Model<'Group'>> = [];

    data.Regattas.forEach(({
      RegattaGroups,
    }) => {
      RegattaGroups.forEach(({
        Group,
      }) => {
        if (Group) {
          output.push(Group);
        }
      });
    });

    const uniqueGroups: Array<$Model<'Group'>> = _.uniqBy(
      output,
      'id',
    );

    const sortedGroups: Array<$Model<'Group'>> = _.sortBy(
      uniqueGroups,
      'Name',
    );

    return sortedGroups;
  }

  async setActiveRegionSeason({
    RegionSeasonId,
  }: {
    RegionSeasonId: string;
  }): Promise<void> {
    const {
      main,
    } = this.props;

    const latestRegionSeason: boolean = isLatestRegionSeason({
      regions: main.regions,
      RegionSeasonId,
    });

    await this.setStateAsync({
      activeRegionSeasonId: RegionSeasonId,
      filteredData: [],
      filterValue: '',
      filterView: 'search',
      showFullList: latestRegionSeason === false,
    });

    this.updatePageUrl();

    await this.getData();
  }

  async setLocation(location: $GeoLocation): Promise<void> {
    const {
      data,
    } = this.state;

    const regionSeasonRegattaLocation: $Model<'RegionSeasonRegattaLocation'> | undefined = _.find(
      data.Locations,
      {
        GeoLocationLat: location.lat,
        GeoLocationLng: location.lng,
      },
    );

    if (regionSeasonRegattaLocation) {
      const value: string = regionSeasonRegattaLocation.Location;

      await this.setStateAsync({
        filterValue: value,
        filterView: 'map',
      });

      await sleep(200);

      scrollTop(listSubSectionId);

      await this.filterDataBySearch(value);
    }
  }

  updatePageUrl(): void {
    const {
      main,
    } = this.props;
    const {
      activeRegionSeasonId,
    } = this.state;

    try {
      const regionSeason = getRegionSeasonByRegionSeasonId({
        regions: main.regions,
        RegionSeasonId: activeRegionSeasonId,
      });

      Services.Navigation.replace({
        pathname: `/${regionSeason.Name}`,
      });
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);
    }
  }

  async filterDataByGroup(groupId: string): Promise<void> {
    const {
      data,
      showFullList,
    } = this.state;

    await this.setStateAsync({
      filteredData: [],
      loading: true,
      showFullList,
    });

    let filteredData = data.Regattas;

    if (groupId !== '') {
      filteredData = data.Regattas.filter((
        regatta: $FullWebRegatta,
      ) => regatta.RegattaGroups.filter((
        regattaGroup: $FullRegattaGroup,
      ) => regattaGroup.Group.id === groupId).length !== 0);
    }

    await sleep(300);

    await this.setStateAsync({
      filteredData,
      loading: false,
      showFullList: true,
    });
  }

  async filterDataBySearch(search: string): Promise<void> {
    const {
      data,
    } = this.state;

    await this.setStateAsync({
      filteredData: [],
      loading: true,
    });

    const filterValue = search.toLowerCase();

    const filteredData = data.Regattas.filter(({
      Location,
      Name,
      OrganizerName,
      Slug,
    }: $FullWebRegatta) => {
      const items = [Name, Slug, OrganizerName, Location];

      const matches: boolean = items.filter((item) => {
        if (item) {
          const itemValue = item.toLowerCase();

          if (itemValue.includes(filterValue)) {
            return true;
          }
        }

        return false;
      }).length !== 0;

      return matches;
    });

    await sleep(300);

    await this.setStateAsync({
      filteredData,
      loading: false,
      showFullList: true,
    });
  }

  async init(): Promise<void> {
    const {
      main,
    } = this.props;
    const {
      activeRegionSeasonId,
    } = this.state;

    const latestRegionSeason: boolean = isLatestRegionSeason({
      regions: main.regions,
      RegionSeasonId: activeRegionSeasonId,
    });

    await this.setStateAsync({
      showFullList: latestRegionSeason === false,
    });

    await this.getData();
  }

  renderHeader() {
    const {
      main,
      t,
    } = this.props;
    const {
      activeRegionSeasonId,
    } = this.state;

    const region: $FullRegion = getRegionByRegionSeasonId({
      regions: main.regions,
      RegionSeasonId: activeRegionSeasonId,
    });

    return (
      <div className={'bg-white rounded-lg p-5 mb-3'}>
        <div className={'flex flex-row items-center'}>
          <div className={'custom-select mr-3'}>
            <select
              className={'form-control rounded-full btn_primary btn_outlined font-bold'}
              name={'RegionSeasonId'}
              onChange={(e) => this.setActiveRegionSeason({
                RegionSeasonId: e.currentTarget.value,
              })}
              value={activeRegionSeasonId}
            >
              {region.RegionSeasons.map((item) => (
                <option
                  key={item.id}
                  value={item.id}
                >
                  {item.Name}
                </option>
              ))}
            </select>
          </div>
          <h2 className={'h3 grow flex flex-row items-center'}>
            {t('Season regattas')}
          </h2>
          <div className={'flex flex-row justify-end'}>
            <div className={'hidden md:block'}>
              {this.renderFilterInputs()}
            </div>
            {/*
            <a
              className="btn btn_primary btn_outlined uppercase px-2 py-1 ml-3"
              href={`${config.CDN_URL}/ics/regattas/${region.id}`}
              target="_blank"
              rel="noreferrer"
            >
              <em className="la la-calendar text-2xl" />
            </a>
            */}
          </div>
        </div>
      </div>
    );
  }

  renderTimelineSubSection() {
    const {
      main,
      redirect,
    } = this.props;
    const {
      activeRegionSeasonId,
      data,
      subSectionView,
    } = this.state;

    if (subSectionView === 'timeline') {
      const basePath: string = getLocalItemBasePath({
        basePath: '/regatta',
        regions: main.regions,
        RegionSeasonId: activeRegionSeasonId,
      });

      const region: $FullRegion = getRegionByRegionSeasonId({
        regions: main.regions,
        RegionSeasonId: activeRegionSeasonId,
      });

      const groups = data.Locations.map((dataItem: $Model<'RegionSeasonRegattaLocation'>) => ({
        content: dataItem.Location,
        id: dataItem.id,
      }));

      groups.push({
        content: '',
        id: 'undefined',
      });

      const regattasDates: Array<string> = [];

      const items = data.Regattas.map((dataItem: $FullWebRegatta) => {
        const startDate: string = moment(dataItem.StartDate).toString();
        const endDate: string = moment(dataItem.EndDate).toString();

        regattasDates.push(startDate);
        regattasDates.push(endDate);

        const date: string = formatDateRange({
          end: dataItem.EndDate,
          shortDateFormat: region.ShortDateFormat,
          start: dataItem.StartDate,
        });

        return {
          className: `badge badge_outlined badge_${statusLabelClass[dataItem.Status]} uppercase py-1`,
          content: dataItem.Name,
          end: dataItem.EndDate,
          group: _.get(
            _.find(
              groups,
              {
                content: dataItem.Location,
              },
            ),
            'id',
            'undefined',
          ),
          id: dataItem.id,
          start: dataItem.StartDate,
          title: `${date}   ${dataItem.Name}`,
        };
      });

      const range = getDateRange({
        data: regattasDates,
        extents: true,
        timezone: region.Timezone,
      });

      const now: moment.Moment = moment();

      const weekBeforeNow: moment.Moment = now.subtract(
        1,
        'week',
      );

      let end = range.max;
      let start;

      if (range.min && now.isBefore(moment(range.max))) {
        start = range.min;

        if (weekBeforeNow.isAfter(moment(range.min))) {
          start = weekBeforeNow.toDate();
        }

        end = moment(start).add(
          1,
          'months',
        ).toDate();
      }

      const options = {
        ...range,
        cluster: {
          maxItems: 1,
        },
        end,
        groupTemplate: (item) => {
          if (item && item.content !== '') {
            const label = document.createElement('button');

            label.className = 'btn btn_primary btn_outlined uppercase whitespace-nowrap px-2 py-0';
            label.innerHTML = item.content;
            label.addEventListener(
              'click',
              async () => {
                const dataLocation = _.find(
                  data.Locations,
                  {
                    id: item.id,
                  },
                );

                if (dataLocation) {
                  const location: $GeoLocation = {
                    lat: dataLocation.GeoLocationLat || 0,
                    lng: dataLocation.GeoLocationLng || 0,
                  };

                  await this.setStateAsync({
                    reCenter: location,
                    subSectionView: 'map',
                  });

                  await this.setLocation(location);
                }
              },
            );

            return label;
          }

          return '';
        },
        locale: i18next.language,
        orientation: 'top',
        selectable: false,
        showWeekScale: true,
        stack: true,
        start,
      };

      return (
        <div
          className={'rounded-lg bg-white p-3 mb-3'}
          id={'regattas-timeline'}
        >
          <Timeline
            clickHandler={(e) => {
              if (e.item) {
                const item = data.Regattas.find((dataItem) => dataItem.id === e.item);

                if (item) {
                  const pathname: string = `${basePath}/${item.Slug || item.id}`;

                  redirect({
                    pathname,
                  });
                }
              }
            }}
            initialGroups={groups}
            initialItems={items}
            options={options}
          />
        </div>
      );
    }

    return null;
  }

  renderMapMapSubSection() {
    const {
      main,
      setRegion,
    } = this.props;
    const {
      activeRegionSeasonId,
      data,
      reCenter,
      regionsGeoData,
    } = this.state;

    const region: $FullRegion = getRegionByRegionSeasonId({
      regions: main.regions,
      RegionSeasonId: activeRegionSeasonId,
    });

    const locations = data.Locations.map(({
      GeoLocationLat,
      GeoLocationLng,
    }: $Model<'RegionSeasonRegattaLocation'>): $GeoLocation => ({
      lat: GeoLocationLat || 0,
      lng: GeoLocationLng || 0,
    })).filter(({
      lat,
      lng,
    }: $GeoLocation) => lat && lng);

    return (
      <MapCluster
        activeRegionFlag={region.Flag}
        locations={locations}
        onLocationSelect={(location: $GeoLocation) => this.setLocation(location)}
        onRegionSelect={(flag: string) => {
          const selectedRegion = _.find(
            main.regions,
            {
              Flag: flag,
            },
          );

          if (selectedRegion) {
            setRegion({
              RegionId: selectedRegion.id,
            });
          }
        }}
        reCenter={reCenter}
        regions={regionsGeoData}
      />
    );
  }

  renderMapLocationsSubSection() {
    const {
      data,
      filterValue,
    } = this.state;

    return (
      <div className={'px-5 py-3'}>
        <div className={'flex flex-row overflow-x-auto'}>
          {data.Locations.map((dataItem: $Model<'RegionSeasonRegattaLocation'>) => (
            <button
              className={`btn btn_primary ${filterValue === dataItem.Location ? 'btn_active' : 'btn_outlined'} uppercase whitespace-nowrap px-2 py-1 mr-3`}
              key={dataItem.id}
              onClick={async () => {
                const location: $GeoLocation = {
                  lat: dataItem.GeoLocationLat || 0,
                  lng: dataItem.GeoLocationLng || 0,
                };

                await this.setStateAsync({
                  reCenter: location,
                });

                await this.setLocation(location);
              }}
              type={'button'}
            >
              {dataItem.Location}
            </button>
          ))}
        </div>
      </div>
    );
  }

  renderMapSubSection() {
    const {
      regionsLoading,
      subSectionView,
    } = this.state;

    return (subSectionView === 'map' ? (
      <div
        className={`rounded-lg bg-white mb-3 ${regionsLoading === true ? 'h-24' : ''}`}
        id={'regattas-map'}
      >
        <Loader
          fade
          loading={regionsLoading}
        >
          <div className={'overflow-hidden'}>
            {this.renderMapMapSubSection()}
            {this.renderMapLocationsSubSection()}
          </div>
        </Loader>
      </div>
    ) : null);
  }

  renderSearchInput() {
    const {
      t,
    } = this.props;
    const {
      filterValue,
      filterView,
      reCenter,
    } = this.state;

    const search = _.debounce(
      async (value: string) => {
        await this.setStateAsync({
          filterView: 'search',
          reCenter: filterView === 'map' ? new Date() : reCenter,
        });

        await this.filterDataBySearch(value);
      },
      1000,
    );

    return (
      <label
        className={'form-control-addon-within rounded-full btn_primary btn_outlined mr-3'}
        htmlFor={'search-regattas'}
      >
        <input
          aria-label={t('Search season regattas')}
          className={'form-control border-none h-full pr-0'}
          id={'search-regattas'}
          onChange={async (e) => {
            const {
              value,
            } = e.target;

            await this.setStateAsync({
              filterValue: value,
            });

            await search(value);
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              // @ts-expect-error blur issue
              e.target.blur();
            }
          }}
          placeholder={t('Search')}
          value={filterValue}
        />
        <span className={'btn btn-link text-blue-900-i text-2xl leading-none la la-search ml-auto m-0 px-2'} />
      </label>
    );
  }

  renderGroupInput() {
    const groups = this.getGroups();

    return (
      <FilterSelect
        className={'w-full mr-3'}
        data={groups.map((Group: $Model<'Group'>) => ({
          Name: Group.Name,
          Value: Group.id,
        }))}
        setItem={async (value) => {
          await this.setStateAsync({
            filterValue: value,
            reCenter: new Date(),
          });

          await this.filterDataByGroup(value);
        }}
      />
    );
  }

  renderFilterInputLeft() {
    const {
      filterView,
    } = this.state;

    if (filterView === 'group') {
      return this.renderGroupInput();
    }

    return this.renderSearchInput();
  }

  renderFilterInputRight() {
    const {
      data,
      filterValue,
      filterView,
    } = this.state;

    if (filterView === 'map' || (filterValue !== '' && ['group', 'search'].includes(filterView))) {
      return (
        <button
          className={'btn btn_danger btn_outlined uppercase px-2 py-1'}
          onClick={async () => {
            await this.setStateAsync({
              filteredData: [],
              loading: true,
              showFullList: false,
            });

            if (filterView === 'map') {
              scrollTop(webSections[webPages.regattasPage].regattas);
            }

            await sleep(300);

            await this.setStateAsync({
              filteredData: data.Regattas,
              filterValue: '',
              filterView: 'search',
              loading: false,
              reCenter: new Date(),
            });
          }}
          type={'button'}
        >
          <em className={'la la-close text-2xl'} />
        </button>
      );
    }

    return (filterView === 'group' ? (
      <button
        className={'btn btn_primary btn_outlined uppercase px-2 py-1'}
        onClick={() => this.setState({
          filteredData: data.Regattas,
          filterView: 'search',
        })}
        type={'button'}
      >
        <em className={'la la-search text-2xl'} />
      </button>
    ) : (
      <button
        className={'btn btn_primary btn_outlined uppercase px-2 py-1'}
        onClick={() => this.setState({
          filteredData: data.Regattas,
          filterView: 'group',
        })}
        type={'button'}
      >
        <em className={'la la-filter text-2xl'} />
      </button>
    ));
  }

  renderFilterInputs() {
    const {
      data,
      regionsGeoData,
      subSectionView,
    } = this.state;

    return (
      <div className={'flex flex-row'}>
        <div className={' flex flex-row sm:max-w-xs'}>
          {this.renderFilterInputLeft()}
          {this.renderFilterInputRight()}
        </div>
        <div className={'flex flex-row justify-end ml-auto'}>
          <button
            className={`btn btn_primary btn_outlined uppercase px-2 py-1 ml-3 ${subSectionView === 'map' ? 'btn_active' : ''}`}
            onClick={async () => {
              const isOpen: boolean = subSectionView !== 'map';

              if (isOpen === true) {
                await this.setStateAsync({
                  filteredData: data.Regattas,
                  filterValue: '',
                  filterView: 'search',
                  subSectionView: 'map',
                });

                if (regionsGeoData.length === 0) {
                  await this.getRegionsData();
                }

                await this.setStateAsync({
                  reCenter: new Date(),
                });
              } else {
                await this.setStateAsync({
                  subSectionView: null,
                });
              }
            }}
            type={'button'}
          >
            <em className={'la la-map text-2xl'} />
          </button>
          <button
            className={`btn btn_primary btn_outlined uppercase px-2 py-1 ml-3 ${subSectionView === 'timeline' ? 'btn_active' : ''}`}
            onClick={async () => {
              this.setState({
                filteredData: data.Regattas,
                filterValue: '',
                filterView: 'search',
                subSectionView: subSectionView === 'timeline' ? null : 'timeline',
              });
            }}
            type={'button'}
          >
            <em className={'la la-calendar text-2xl'} />
          </button>
        </div>
      </div>
    );
  }

  renderFilterSubSection() {
    return (
      <div className={'bg-white rounded-lg p-5 mb-3 md:hidden'}>
        {this.renderFilterInputs()}
      </div>
    );
  }

  // renderListLabel() {
  //   const {
  //     t,
  //   } = this.props;
  //   const {
  //     filterValue,
  //     filterView,
  //   } = this.state;

  //   if (filterValue !== '') {
  //     const label: Record<$FilterView, string> = {
  //       group: 'Group',
  //       map: 'Location',
  //       search: 'Search',
  //     };

  //     let value: string = filterValue;

  //     if (filterView === 'group') {
  //       const groups = this.getGroups();

  //       value = _.get(
  //         _.find(
  //           groups,
  //           {
  //             id: filterValue,
  //           },
  //         ),
  //         'Name',
  //         t('Unknown'),
  //       );
  //     }

  //     return (
  //       <span>
  //         <span className="text-blue-900">
  //           {`${t(label[filterView])}: `}
  //         </span>
  //         <span className="text-gray-600">
  //           {value}
  //         </span>
  //       </span>
  //     );
  //   }

  //   return <span />;
  // }

  renderList() {
    const {
      main,
      t,
    } = this.props;
    const {
      activeRegionSeasonId,
      filteredData,
      showFullList,
    } = this.state;

    const region: $FullRegion = getRegionByRegionSeasonId({
      regions: main.regions,
      RegionSeasonId: activeRegionSeasonId,
    });

    const shortDateFormat: string = region.ShortDateFormat;

    const basePath: string = getLocalItemBasePath({
      basePath: '/regatta',
      regions: main.regions,
      RegionSeasonId: activeRegionSeasonId,
    });

    return (
      <div id={listSubSectionId}>
        {filteredData.length > 0 ? (
          <RegattasList
            basePath={basePath}
            data={filteredData}
            shortDateFormat={shortDateFormat}
            showFullList={showFullList}
          />
        ) : (
          <div className={'bg-white rounded-lg p-5'}>
            <NoData label={t('The list is empty')} />
          </div>
        )}
      </div>
    );
  }

  renderListSubSection() {
    const {
      loading,
    } = this.state;

    return (loading === true ? (
      <EmptyList
        items={[
          [3, 1],
          [3, 1],
          [3, 1],
          [3, 1],
          [3, 1],
          [3, 1],
        ]}
      />
    ) : this.renderList());
  }

  renderContent() {
    return (
      <>
        {this.renderTimelineSubSection()}
        {this.renderMapSubSection()}
        {this.renderFilterSubSection()}
        {this.renderListSubSection()}
      </>
    );
  }

  render() {
    return (
      <Section
        id={webSections[webPages.regattasPage].regattas}
        noMargin
        transparent
      >
        {this.renderHeader()}
        {this.renderContent()}
      </Section>
    );
  }
}

export default RegattasSectionView;
